import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { CheckCircle2, Circle, Pencil, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IServiceRequirementsCardProps } from '@/@types/serviceTypes';
import type { IPolicyFieldValue } from '@/@types/insurance';

const DRIVER_FIELD_PATTERN = /^Driver (\d+) (.+)$/;
const DRIVER_FIELD_NAMES = [
  'Full Name',
  'Birthday',
  'Nationality',
  'License Issue Country',
  'License Issue Date',
  'License Front',
  'License Back',
];
// Base required field labels that correspond to driver fields (without "Driver N" prefix)
const BASE_DRIVER_LABELS = new Set(DRIVER_FIELD_NAMES.map(n => `Driver ${n}`));

function getCategoryLabel(category: string): string {
  switch (category) {
    case 'insurance':
      return 'Insurance';
    case 'real_estate':
      return 'Real Estate';
    default:
      return category;
  }
}

function getCategoryColor(category: string): string {
  switch (category) {
    case 'insurance':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'real_estate':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
}

function FieldItem({
  label,
  collected,
}: {
  // LOCAL_TYPE_JUSTIFIED: inline prop shape for internal sub-component only used here
  label: string;
  collected?: IPolicyFieldValue;
}) {
  const hasValue =
    collected && (collected.value || (collected.storageIds && collected.storageIds.length > 0));

  return (
    <li className="flex items-start gap-2.5">
      {hasValue ? (
        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
      ) : (
        <Circle className="h-4 w-4 text-muted-foreground/40 mt-0.5 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <span className={cn('text-sm', hasValue ? 'text-foreground' : 'text-muted-foreground')}>
          {label}
        </span>
        {hasValue && collected?.value && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{collected.value}</p>
        )}
        {hasValue &&
          collected?.storageIds &&
          collected.storageIds.length > 0 &&
          !collected.value && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {collected.storageIds.length} file(s) attached
            </p>
          )}
      </div>
    </li>
  );
}

export function ServiceRequirementsCard({
  serviceData,
  serviceType,
  onEdit,
}: IServiceRequirementsCardProps) {
  const collectedMap = new Map(serviceData.collectedFields.map(f => [f.fieldLabel, f]));

  // Filter out Provider fields from display and progress
  const visibleFields = serviceType.requiredFields.filter(f => f.fieldType !== 'provider');

  const collectedCount = visibleFields.filter(field => {
    const collected = collectedMap.get(field.label);
    return (
      collected && (collected.value || (collected.storageIds && collected.storageIds.length > 0))
    );
  }).length;

  const totalCount = visibleFields.length;
  const progressPercent = totalCount > 0 ? Math.round((collectedCount / totalCount) * 100) : 0;

  // Detect driver fields in collected data — build a map of driverNum -> fieldName -> collected
  const driverCollectedMap = new Map<number, Map<string, IPolicyFieldValue>>();
  for (const [label, collected] of collectedMap) {
    const match = DRIVER_FIELD_PATTERN.exec(label);
    if (match) {
      const driverNum = parseInt(match[1], 10);
      const fieldName = match[2];
      if (!driverCollectedMap.has(driverNum)) driverCollectedMap.set(driverNum, new Map());
      driverCollectedMap.get(driverNum)!.set(fieldName, collected); // AS_JUSTIFIED: driverNum key was just set above
    }
  }

  const hasDrivers = driverCollectedMap.size > 0;

  // Non-driver visible fields: exclude base driver labels (e.g. "Driver Full Name") when drivers exist
  const regularFields = hasDrivers
    ? visibleFields.filter(f => !BASE_DRIVER_LABELS.has(f.label))
    : visibleFields;

  // Sort driver numbers
  const sortedDriverNums = Array.from(driverCollectedMap.keys()).sort((a, b) => a - b);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-semibold">{serviceType.name}</CardTitle>
            <Badge
              variant="secondary"
              className={cn('text-xs', getCategoryColor(serviceType.category))}
            >
              {getCategoryLabel(serviceType.category)}
            </Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4 mr-1" />
            Edit
          </Button>
        </div>

        <div className="mt-3 space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {collectedCount}/{totalCount} fields collected
            </span>
            <span className="font-medium text-foreground">{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      </CardHeader>

      <Separator />

      <CardContent className="pt-4">
        {hasDrivers ? (
          <div className="grid grid-cols-2 gap-6">
            {/* Left column: regular fields */}
            <ul className="space-y-2">
              {regularFields.map(field => (
                <FieldItem
                  key={field.label}
                  label={field.label}
                  collected={collectedMap.get(field.label)}
                />
              ))}
            </ul>

            {/* Right column: driver sections — always show all 7 fields per driver */}
            <div className="space-y-4">
              {sortedDriverNums.map(num => {
                const driverData = driverCollectedMap.get(num) ?? new Map();
                return (
                  <div key={num}>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Users className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Driver {num}</span>
                    </div>
                    <ul className="space-y-2 pl-1">
                      {DRIVER_FIELD_NAMES.map(fieldName => (
                        <FieldItem
                          key={`driver-${num}-${fieldName}`}
                          label={fieldName}
                          collected={driverData.get(fieldName)}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <ul className="space-y-2">
            {visibleFields.map(field => (
              <FieldItem
                key={field.label}
                label={field.label}
                collected={collectedMap.get(field.label)}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
